import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { env } from 'utils/env';
import { GQL_VERSION_QUERY, API_VERSION_QUERY, DATABASE_VERSION_QUERY, ANAF_VERSION_QUERY } from './queries';
import { REQUEST_DEIMOS_VERSION, ERP_VERSION, PUBLISH_LANGUAGE, RESET_CACHE } from './mutations';
import { ERP_VERSION_DATA, LANGUAGE_PUBLISHED, CACHE_RESET, DEIMOS_VERSION } from './subscriptions';
import { useServerErrorHandling } from 'utils/hooks/useServerErrorHandling';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { useMutation, useSubscription } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { showError, showInfo } from 'actions/actionCreators/snackbarActions';
import * as types from 'actions/eventTypes';
import { Settings, Sync } from '@mui/icons-material';
import { Button, Card } from '@totalsoft/rocket-ui';
import { Typography, Grid } from '@mui/material';
import HeaderPortal from '../layout/HeaderPortal';
import { VersionList, VersionsWrapper } from './SettingsStyle';
import LoadingWrapper from 'components/common/LoadingWrapper';
import { GridActionsWrapper } from 'components/common/CommonStyles';
import { useResetCache } from 'utils/apollo/common';
import { configurationKeys } from 'features/common/constants/configurationKey';
import { ApplicationFeaturesContext } from 'providers/ApplicationFeaturesContext';

function SettingsContainer() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { resetApolloCache } = useResetCache();

  const { anafIntegration } = useContext(ApplicationFeaturesContext);
  const [resetting, setResetting] = useState(false);
  const [publishingLanguage, setPublishingLanguage] = useState(false);

  const [gql, setGql] = useState({ version: 'N/A', date: 'N/A' });
  const { loading: loadingGQL } = useQueryWithErrorHandling(GQL_VERSION_QUERY, {
    onCompleted: e => {
      setGql(e.gqlVersion);
    }
  });

  const [api, setApi] = useState({ version: 'N/A', date: 'N/A' });
  const { loading: loadingAPI } = useQueryWithErrorHandling(API_VERSION_QUERY, {
    onCompleted: e => {
      setApi(e.apiVersion);
    }
  });

  const [databaseVersion, setDatabaseVersion] = useState('N/A');
  const { loading: loadingDatabase } = useQueryWithErrorHandling(DATABASE_VERSION_QUERY, {
    onCompleted: r => {
      setDatabaseVersion(r.databaseVersion);
    }
  });

  const [erp, setERP] = useState({ clientId: 'N/A', version: 'N/A' });
  const [loadingERP, setLoadingERP] = useState(false);
  const [deimosVersion, setDeimosVersion] = useState();

  const { storeKey } = useServerErrorHandling({
    effect: _ => {
      setLoadingERP(false);
    }
  });

  const [erpVersionData] = useMutation(ERP_VERSION, {
    onError: error => {
      dispatch(showError(error.message, error.correlationId));
      setLoadingERP(false);
    },
    onCompleted: r => {
      const { correlationId } = r.erpVersion;
      storeKey({ correlationId });
    }
  });

  const [deimosVersionData] = useMutation(REQUEST_DEIMOS_VERSION, {
    onError: error => {
      dispatch(showError(error.message, error.correlationId));
    }
  });

  const [anaf, setAnaf] = useState({ version: 'N/A', date: 'N/A' });
  useQueryWithErrorHandling(ANAF_VERSION_QUERY, {
    skip: !anafIntegration,
    onCompleted: e => {
      setAnaf(e.anafVersion);
    }
  });

  useEffect(() => {
    setLoadingERP(true);
    erpVersionData();
  }, [erpVersionData]);

  useEffect(() => {
    deimosVersionData();
  }, [deimosVersionData]);

  useSubscription(ERP_VERSION_DATA, {
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      if (data.erpVersionData) {
        setERP(data.erpVersionData);
        setLoadingERP(false);
      }
    }
  });

  useSubscription(DEIMOS_VERSION, {
    onSubscriptionData: input => {
      const { subscriptionData } = input;
      const { data } = subscriptionData;

      if (data.deimosVersionRetrieved) {
        setDeimosVersion(data.deimosVersionRetrieved);
      }
    }
  });

  const [resetCache] = useMutation(RESET_CACHE, {
    onError: error => {
      setResetting(false);
      dispatch(showError(error.message, error.correlationId));
    },
    onCompleted: r => {
      const { correlationId } = r.resetCache;
      storeKey({ correlationId });
    }
  });

  const onResetCache = () => {
    setResetting(true);
    resetCache({ variables: { input: { resetDocumentSecurity: true } } });
  };

  useSubscription(CACHE_RESET, {
    onSubscriptionData: () => {
      resetApolloCache(configurationKeys);
      setResetting(false);
      dispatch(showInfo(t('Notifications.' + types.CACHE_RESET)));
    }
  });

  const [publishLanguage] = useMutation(PUBLISH_LANGUAGE, {
    onError: error => {
      setPublishingLanguage(false);
      dispatch(showError(error.message, error.correlationId));
    },
    onCompleted: r => {
      const { correlationId } = r.publishLanguage;
      storeKey({ correlationId });
    }
  });

  const onPublishLanguage = () => {
    setPublishingLanguage(true);
    publishLanguage();
  };

  useSubscription(LANGUAGE_PUBLISHED, {
    onSubscriptionData: () => {
      setPublishingLanguage(false);
      dispatch(showInfo(t('Notifications.' + types.LANGUAGE_PUBLISHED)));
    }
  });

  return (
    <>
      <Card icon={Settings} title={t('Settings.Versions.Title')}>
        <VersionsWrapper>
          <Grid item container spacing={1} alignItems='center'>
            <Grid item>
              <Typography fontWeight='bold'>{t('Settings.Versions.UIVersionLabel')}:</Typography>
            </Grid>
            <Grid item flexGrow={1}>
              <Typography>
                {t('Settings.Versions.SettingsVersionValue', {
                  data: {
                    version: env.REACT_APP_VERSION,
                    date: t('LONG_DATE', { date: env.REACT_APP_DATE })
                  }
                })}
              </Typography>
            </Grid>
          </Grid>
          <Grid item container spacing={1} alignItems='center'>
            <Grid item>
              <Typography fontWeight='bold'>{t('Settings.Versions.APIVersionLoading')}:</Typography>
            </Grid>
            <Grid item flexGrow={1}>
              <LoadingWrapper loading={loadingAPI}>
                <Typography>
                  {t('Settings.Versions.SettingsVersionValue', {
                    data: {
                      version: api.version,
                      date: t('LONG_DATE', { date: api.date })
                    }
                  })}
                </Typography>
              </LoadingWrapper>
            </Grid>
          </Grid>
          <Grid item container spacing={1} alignItems='center'>
            <Grid item>
              <Typography fontWeight='bold'>{t('Settings.Versions.GQLVersionLoading')}:</Typography>
            </Grid>
            <Grid item flexGrow={1}>
              <LoadingWrapper loading={loadingGQL}>
                <Typography>
                  {t('Settings.Versions.SettingsVersionValue', {
                    data: {
                      version: gql.version,
                      date: t('LONG_DATE', { date: gql.date })
                    }
                  })}
                </Typography>
              </LoadingWrapper>
            </Grid>
          </Grid>
          {deimosVersion && (
            <Grid item container spacing={1} alignItems='center'>
              <Grid item>
                <Typography fontWeight='bold'>{t('Settings.Versions.DeimosVersion')}:</Typography>
              </Grid>
              <Grid item flexGrow={1}>
                <Typography>
                  {t('Settings.Versions.SettingsVersionValue', {
                    data: {
                      version: deimosVersion.version,
                      date: t('LONG_DATE', { date: deimosVersion.date })
                    }
                  })}
                </Typography>
              </Grid>
            </Grid>
          )}
          <Grid item container spacing={1} alignItems='center'>
            <Grid item>
              <Typography fontWeight='bold'>{t('Settings.Versions.DatabaseVersion')}:</Typography>
            </Grid>
            <Grid item flexGrow={1}>
              <LoadingWrapper loading={loadingDatabase}>
                <Typography>{databaseVersion}</Typography>
              </LoadingWrapper>
            </Grid>
          </Grid>
          <Grid item>
            <Typography fontWeight='bold'>{t('Settings.Versions.ERPVersion.Title')}:</Typography>
            <LoadingWrapper loading={loadingERP}>
              <VersionList>
                <li>
                  <Typography>
                    {t('Settings.Versions.ERPVersion.ClientId')}: {erp.clientId}
                  </Typography>
                </li>
                <li>
                  <Typography>{erp.version}</Typography>
                </li>
              </VersionList>
            </LoadingWrapper>
          </Grid>
          {anafIntegration && (anaf.version || anaf.lastUpdateDate) && (
            <Grid item container spacing={1} alignItems='center'>
              <Grid item>
                <Typography fontWeight='bold'>{t('Settings.Versions.ANAFVersion')}:</Typography>
              </Grid>
              <Grid item flexGrow={1}>
                <LoadingWrapper loading={loadingAPI}>
                  <Typography>
                    {t('Settings.Versions.SettingsVersionValue', {
                      data: {
                        version: anaf.version,
                        date: t('LONG_DATE', { date: anaf.date })
                      }
                    })}
                  </Typography>
                </LoadingWrapper>
              </Grid>
            </Grid>
          )}
        </VersionsWrapper>
      </Card>
      <Card icon={Sync} title={t('Settings.InvalidateCache.Title')}>
        <GridActionsWrapper>
          <Button wd color='primary' onClick={onResetCache} disabled={resetting}>
            {resetting ? t('Settings.InvalidateCache.ResettingCache') : t('Settings.InvalidateCache.ResetCache')}
          </Button>
          <Button wd color='primary' onClick={onPublishLanguage} disabled={publishingLanguage}>
            {publishingLanguage ? t('Settings.InvalidateCache.PublishingLanguage') : t('Settings.InvalidateCache.PublishLanguage')}
          </Button>
        </GridActionsWrapper>
      </Card>
      <HeaderPortal />
    </>
  );
}

SettingsContainer.propTypes = {};

export default SettingsContainer;
